/*
  Cart drawer — sections/tu-cart-drawer.liquid, snippets/tu-cart-drawer-line.liquid.
  Docs/status/plan-2026-09-14-cart-drawer.md.

  One instance (`[data-tu-cd]` is rendered once, from layout/theme.liquid).
  The drawer never builds its own markup from JSON: every refresh re-fetches
  the section (`/cart?section_id=<id>`) and swaps in the head count + body it
  returns, so the drawer can never disagree with the cart Shopify actually
  holds -- the same reason assets/tu-cart.js chose plain submits on the page.

  Four moving parts:
    open()/close()      focus trap, html.tu-lock, closes the mobile nav
                         drawer first if it is open (only one drawer at a
                         time).
    refresh()            re-fetches the section, swaps the body + title
                         count, updates every `.tu-cartlink__n` from the
                         section's own `data-count` (one request, not two).
    add / change         intercepts `form[action*="/cart/add"]` submits and
                         the drawer's own steppers/remove buttons; posts to
                         `/cart/add.js` / `/cart/change.js`, then refresh()s.
    header link           hover-intent on desktop (mirrors TuMegaNav's
                         numbers in assets/tu-header.js); click opens on
                         touch, is left alone on desktop (navigates to
                         `/cart`).

  Without this file: forms post and land on `/cart` exactly as before it
  existed -- nothing here changes the no-JS path.
*/
(function () {
  'use strict';

  var DEBOUNCE_MS = 250;
  var CLOSE_MS = 260;

  function TuCartDrawer(root) {
    this.root = root;
    this.scrims = Array.prototype.slice.call(root.querySelectorAll('[data-tu-cd-close]'));
    this.panel = root.querySelector('.tu-cd__panel');
    this.body = root.querySelector('[data-tu-cd-body]');
    this.foot = root.querySelector('[data-tu-cd-foot]'); // 2026-10-03: outside the body, so Checkout never scrolls away
    this.title = root.querySelector('#tu-cd-title');
    this.status = root.querySelector('[data-tu-cd-status]');
    this.sectionId = root.dataset.sectionId || 'tu-cart-drawer';
    if (!this.panel || !this.body || !this.title) return;

    this.isOpen = false;
    this.openToken = 0;
    this.opener = null;
    this.pending = {}; // per-line debounce timers, keyed by line key
    this.boundKeydown = this.onKeydown.bind(this);

    this.scrims.forEach(function (el) {
      el.addEventListener('click', this.close.bind(this));
    }, this);

    this.body.addEventListener('click', this.onBodyClick.bind(this));
    this.body.addEventListener('change', this.onBodyChange.bind(this));
    // 2026-10-03: "Continue shopping" lives in the foot, which refresh() re-renders, so its close is delegated.
    if (this.foot) {
      this.foot.addEventListener('click', function (e) {
        if (e.target.closest && e.target.closest('[data-tu-cd-close]')) this.close();
      }.bind(this));
    }
    // 2026-10-03: the list scrolls with no visible scrollbar; a fade at a cut edge shows there is more.
    this.body.addEventListener('scroll', this.updateFade.bind(this), { passive: true });

    this.bindAdd();
    this.bindHeaderLink();
  }

  TuCartDrawer.prototype.updateFade = function () {
    var b = this.body;
    this.body.classList.toggle('is-fa', b.scrollTop > 2);
    this.body.classList.toggle('is-fb', b.scrollTop + b.clientHeight < b.scrollHeight - 2);
  };

  TuCartDrawer.prototype.focusables = function () {
    return Array.prototype.slice
      .call(this.panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled])'))
      .filter(function (el) { return el.offsetParent !== null; });
  };

  TuCartDrawer.prototype.open = function () {
    if (this.isOpen) return;
    // Only one drawer at a time -- close the mobile nav drawer first if it
    // is open (plan Task 2 Step 1); its own close() unlocks html.tu-lock
    // synchronously, so ours below re-adds it cleanly.
    var navClose = document.querySelector('.tu-drawer.is-open [data-tu-drawer-close]');
    if (navClose) navClose.click();

    this.isOpen = true;
    this.openToken += 1;
    this.opener = document.activeElement;
    this.root.hidden = false;
    requestAnimationFrame(function () { this.root.classList.add('is-open'); this.updateFade(); }.bind(this));
    document.documentElement.classList.add('tu-lock');
    document.addEventListener('keydown', this.boundKeydown);
    var close = this.root.querySelector('.tu-cd__close');
    if (close) close.focus();
  };

  TuCartDrawer.prototype.close = function () {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.openToken += 1;
    var token = this.openToken;
    this.root.classList.remove('is-open');
    document.documentElement.classList.remove('tu-lock');
    document.removeEventListener('keydown', this.boundKeydown);
    var finish = function () {
      if (token !== this.openToken) return; // reopened before this fired
      this.root.hidden = true;
      this.root.classList.remove('is-added'); // opened later from the header link, it is just "Your cart"
    }.bind(this);
    this.panel.addEventListener('transitionend', finish, { once: true });
    setTimeout(finish, CLOSE_MS + 40); // reduced-motion / dropped-transitionend fallback
    if (this.opener && this.opener.isConnected) this.opener.focus();
    this.opener = null;
  };

  TuCartDrawer.prototype.onKeydown = function (e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      this.close();
      return;
    }
    if (e.key !== 'Tab') return;
    var items = this.focusables();
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  TuCartDrawer.prototype.setStatus = function (msg) {
    if (this.status) this.status.textContent = msg;
  };

  // -- Refresh: the section is the single source of truth -------------------

  TuCartDrawer.prototype.refresh = function () {
    var self = this;
    return fetch('/cart?section_id=' + this.sectionId, { credentials: 'same-origin' })
      .then(function (res) { return res.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newRoot = doc.querySelector('[data-tu-cd]');
        var newBody = doc.querySelector('[data-tu-cd-body]');
        var newTitle = doc.querySelector('#tu-cd-title');
        if (newBody) self.body.innerHTML = newBody.innerHTML;
        if (newTitle) self.title.innerHTML = newTitle.innerHTML;
        var newFoot = doc.querySelector('[data-tu-cd-foot]');
        if (self.foot && newFoot) {
          self.foot.innerHTML = newFoot.innerHTML;
          self.foot.hidden = newFoot.hasAttribute('hidden');
        }
        // 2026-10-03: the foot's Shop Pay / Apple Pay buttons arrive as fresh markup; Shopify's
        // script only wires up buttons it has seen, so re-run it, as Empire did after inserting its own.
        if (window.Shopify && window.Shopify.PaymentButton) window.Shopify.PaymentButton.init();
        var count = newRoot ? newRoot.dataset.count : null;
        if (count !== null) {
          self.root.dataset.count = count;
          document.querySelectorAll('.tu-cartlink__n').forEach(function (el) {
            el.textContent = count;
            el.setAttribute('aria-label', count + ' items in cart');
          });
        }
        // 2026-10-06, skeleton sweep: the new line photos are fresh <img>s; hand them to tu-card.js so they shimmer until loaded.
        if (window.TuSkel) window.TuSkel.observe(self.body);
        self.animateLines();
        self.updateFade();
        return count;
      });
  };

  // Stagger the freshly-inserted lines in, 30ms apart -- same vocabulary as
  // `.tu-drawer__row` (tu-motion block, assets/tu-system.css). A CSS-only
  // opacity/transform rule keyed off `.is-open` cannot re-trigger on a body
  // swapped in by innerHTML (the browser never renders the "before" frame),
  // so this toggles a class the same way `.tu-rv__card--anim` does.
  TuCartDrawer.prototype.animateLines = function () {
    var lines = this.body.querySelectorAll('.tu-cd__line');
    lines.forEach(function (el, i) {
      el.style.setProperty('--i', i);
      el.classList.remove('is-in');
    });
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        lines.forEach(function (el) { el.classList.add('is-in'); });
      });
    });
  };

  // -- Add: intercept form[action*="/cart/add"] submits ----------------------

  TuCartDrawer.prototype.submitControls = function (form) {
    var controls = Array.prototype.slice.call(form.querySelectorAll('button[type="submit"], input[type="submit"]'));
    if (form.id) {
      Array.prototype.slice.call(document.querySelectorAll('[form="' + form.id + '"]')).forEach(function (el) {
        if (controls.indexOf(el) === -1) controls.push(el);
      });
    }
    return controls;
  };

  TuCartDrawer.prototype.showError = function (form, msg) {
    var controls = this.submitControls(form);
    var anchor = controls[0] || form;
    var err = anchor.parentNode && anchor.parentNode.querySelector(':scope > .tu-cd__err');
    if (!err) {
      err = document.createElement('p');
      err.className = 'tu-cd__err';
      if (anchor.parentNode) anchor.parentNode.insertBefore(err, anchor.nextSibling);
    }
    err.textContent = msg;
    err.hidden = false;
  };

  TuCartDrawer.prototype.clearError = function (form) {
    var controls = this.submitControls(form);
    controls.forEach(function (btn) {
      var err = btn.parentNode && btn.parentNode.querySelector(':scope > .tu-cd__err');
      if (err) err.hidden = true;
    });
  };

  TuCartDrawer.prototype.bindAdd = function () {
    var self = this;
    // 2026-09-20: on WINDOW, capture phase, and the event is stopped. Shopify's
    // storefront tracker (trekkie, loaded by content_for_header) hooks BOTH a
    // document-level submit on `form[action*="/cart/add"]` ("add-form") AND the
    // fetch to /cart/add.js ("add-fetch"), so a submit that is intercepted and
    // re-sent as fetch produced two `product_added_to_cart` events per add --
    // every app pixel (GA4 add_to_cart, Meta AddToCart, Logical Position) got
    // both. GA4 showed 2% of cart-adders with a single event after the publish
    // vs 66% under Empire (Docs/evidence/ga4-baseline.md). Window capture runs
    // before document listeners; stopImmediatePropagation keeps the tracker's
    // form hook from seeing the submit, leaving the fetch hook as the one
    // event. Verified on the mirror: one add -> one event, one item.
    window.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form.matches || !form.matches('form[action*="/cart/add"]')) return;
      if (self.root.dataset.openOnAdd === 'false') return; // let it navigate as before

      e.preventDefault();
      e.stopImmediatePropagation();
      var controls = self.submitControls(form);
      controls.forEach(function (btn) { btn.disabled = true; });
      self.clearError(form);

      fetch('/cart/add.js', {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'application/json' },
      })
        .then(function (res) { return res.json().then(function (data) { return { ok: res.ok, data: data }; }); })
        .then(function (result) {
          controls.forEach(function (btn) { btn.disabled = false; });
          if (result.ok) {
            document.dispatchEvent(new CustomEvent('tu:cart:changed', { detail: result.data }));
            return self.refresh().then(function () {
              self.setStatus('Added to cart');
              self.root.classList.add('is-added'); // "Added to your cart" row; cleared on close
              self.open();
            });
          }
          self.showError(form, result.data.description || result.data.message || 'Could not add that to the cart.');
        })
        .catch(function () {
          controls.forEach(function (btn) { btn.disabled = false; });
          self.showError(form, 'Could not add that to the cart.');
        });
    }, true);
  };

  // -- Change: steppers + remove inside the drawer ----------------------------

  TuCartDrawer.prototype.lineFor = function (key) {
    return this.body.querySelector('[data-tu-cd-line][data-key="' + key + '"]');
  };

  TuCartDrawer.prototype.postChange = function (key, quantity) {
    var self = this;
    var line = this.lineFor(key);
    if (line) line.classList.add('is-busy');
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity }),
    })
      .then(function (res) { return res.json().then(function (data) { return { ok: res.ok, data: data }; }); })
      .then(function (result) {
        if (result.ok) {
          document.dispatchEvent(new CustomEvent('tu:cart:changed', { detail: result.data }));
          self.setStatus(quantity === 0 ? 'Removed from cart' : 'Cart updated');
        }
        return self.refresh();
      })
      .catch(function () { return self.refresh(); });
  };

  // Debounced so a fast double-press of +/- sends one request with the
  // last value, not two racing ones (plan Task 2 Step 4).
  TuCartDrawer.prototype.scheduleChange = function (key, quantity) {
    var self = this;
    clearTimeout(this.pending[key]);
    this.pending[key] = setTimeout(function () {
      delete self.pending[key];
      self.postChange(key, quantity);
    }, DEBOUNCE_MS);
  };

  TuCartDrawer.prototype.onBodyClick = function (e) {
    var stepBtn = e.target.closest && e.target.closest('[data-tu-cd-qty-step]');
    if (stepBtn) {
      var group = stepBtn.closest('[data-tu-cd-qty]');
      // 2026-10-03: the quantity is text ([data-tu-cd-qty-value], design D); the input path stays for safety.
      var input = group && group.querySelector('[data-tu-cd-qty-input]');
      var value = group && group.querySelector('[data-tu-cd-qty-value]');
      if (!input && !value) return;
      var step = parseInt(stepBtn.getAttribute('data-tu-cd-qty-step'), 10) || 0;
      var now = parseInt(input ? input.value : value.textContent, 10);
      if (isNaN(now)) now = 0;
      var next = Math.max(value ? 1 : 0, now + step); // with the text control, − stops at 1; the bin removes
      if (next === now) return;
      if (input) input.value = String(next);
      if (value) value.textContent = String(next);
      var minus = group.querySelector('[data-tu-cd-qty-step="-1"]');
      if (minus && value) minus.disabled = next <= 1;
      this.scheduleChange(group.dataset.key, next);
      return;
    }

    var removeBtn = e.target.closest && e.target.closest('[data-tu-cd-remove]');
    if (removeBtn) {
      this.scheduleChange(removeBtn.dataset.key, 0);
    }
  };

  TuCartDrawer.prototype.onBodyChange = function (e) {
    var input = e.target.closest && e.target.closest('[data-tu-cd-qty-input]');
    if (!input) return;
    var v = parseInt(input.value, 10);
    if (isNaN(v) || v < 0) v = 0;
    input.value = String(v);
    var group = input.closest('[data-tu-cd-qty]');
    if (group) this.scheduleChange(group.dataset.key, v);
  };

  // -- Header cart link: hover-intent on desktop, click-to-open on touch -----

  TuCartDrawer.prototype.bindHeaderLink = function () {
    var self = this;
    var link = document.querySelector('.tu-cartlink');
    if (!link) return;
    var hoverMQ = window.matchMedia('(hover: hover) and (pointer: fine)');
    var openTimer = null;
    var closeTimer = null;

    function hoverActive() {
      return self.root.dataset.hoverOpen !== 'false' && hoverMQ.matches;
    }

    function stay() {
      clearTimeout(closeTimer);
    }
    function scheduleClose() {
      if (!hoverActive()) return;
      clearTimeout(openTimer);
      closeTimer = setTimeout(function () { self.close(); }, 400);
    }

    link.addEventListener('pointerenter', function () {
      if (!hoverActive()) return;
      stay();
      clearTimeout(openTimer);
      openTimer = setTimeout(function () {
        self.refresh().then(function () { self.open(); });
      }, 150);
    });
    link.addEventListener('pointerleave', scheduleClose);
    self.panel.addEventListener('pointerenter', stay);
    self.panel.addEventListener('pointerleave', scheduleClose);

    link.addEventListener('click', function (e) {
      if (hoverActive()) return; // desktop: click still navigates to /cart
      e.preventDefault();
      self.refresh().then(function () { self.open(); });
    });
  };

  function init() {
    var root = document.querySelector('[data-tu-cd]');
    if (!root || root.tuBound) return;
    root.tuBound = true;
    new TuCartDrawer(root);
  }

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
}());
